import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getProducts } from 'app/entities/product/product.reducer';
import { ProcessType } from 'app/shared/model/enumerations/process-type.model';
import { StepType } from 'app/shared/model/enumerations/step-type.model';
import { StepStatus } from 'app/shared/model/enumerations/step-status.model';
import { createEntity, getEntity, reset, updateEntity } from './product-process.reducer';

export const ProductProcessUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const products = useAppSelector(state => state.product.entities);
  const productProcessEntity = useAppSelector(state => state.productProcess.entity);
  const loading = useAppSelector(state => state.productProcess.loading);
  const updating = useAppSelector(state => state.productProcess.updating);
  const updateSuccess = useAppSelector(state => state.productProcess.updateSuccess);
  const processTypeValues = Object.keys(ProcessType);
  const stepTypeValues = Object.keys(StepType);
  const stepStatusValues = Object.keys(StepStatus);

  const handleClose = () => {
    navigate('/product-process');
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getProducts({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    if (values.retryCount !== undefined && typeof values.retryCount !== 'number') {
      values.retryCount = Number(values.retryCount);
    }
    values.createdAt = convertDateTimeToServer(values.createdAt);
    values.updatedAt = convertDateTimeToServer(values.updatedAt);

    const entity = {
      ...productProcessEntity,
      ...values,
      product: products.find(it => it.id.toString() === values.product?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          createdAt: displayDefaultDateTime(),
          updatedAt: displayDefaultDateTime(),
        }
      : {
          processType: 'MOVE_IN',
          stepType: 'CREATED',
          stepStatus: 'PENDING',
          ...productProcessEntity,
          createdAt: convertDateTimeFromServer(productProcessEntity.createdAt),
          updatedAt: convertDateTimeFromServer(productProcessEntity.updatedAt),
          product: productProcessEntity?.product?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="crawlerBackendApp.productProcess.home.createOrEditLabel" data-cy="ProductProcessCreateUpdateHeading">
            <Translate contentKey="crawlerBackendApp.productProcess.home.createOrEditLabel">
              Create or edit a ProductProcess
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="product-process-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('crawlerBackendApp.productProcess.processType')}
                id="product-process-processType"
                name="processType"
                data-cy="processType"
                type="select"
              >
                {processTypeValues.map(processType => (
                  <option value={processType} key={processType}>
                    {translate(`crawlerBackendApp.ProcessType.${processType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.productProcess.stepType')}
                id="product-process-stepType"
                name="stepType"
                data-cy="stepType"
                type="select"
              >
                {stepTypeValues.map(stepType => (
                  <option value={stepType} key={stepType}>
                    {translate(`crawlerBackendApp.StepType.${stepType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.productProcess.stepStatus')}
                id="product-process-stepStatus"
                name="stepStatus"
                data-cy="stepStatus"
                type="select"
              >
                {stepStatusValues.map(stepStatus => (
                  <option value={stepStatus} key={stepStatus}>
                    {translate(`crawlerBackendApp.StepStatus.${stepStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.productProcess.retryCount')}
                id="product-process-retryCount"
                name="retryCount"
                data-cy="retryCount"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcess.payloadIn')}
                id="product-process-payloadIn"
                name="payloadIn"
                data-cy="payloadIn"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcess.payloadOut')}
                id="product-process-payloadOut"
                name="payloadOut"
                data-cy="payloadOut"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcess.coolingOffEnd')}
                id="product-process-coolingOffEnd"
                name="coolingOffEnd"
                data-cy="coolingOffEnd"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcess.offerExpirationDate')}
                id="product-process-offerExpirationDate"
                name="offerExpirationDate"
                data-cy="offerExpirationDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcess.mandateSignedDate')}
                id="product-process-mandateSignedDate"
                name="mandateSignedDate"
                data-cy="mandateSignedDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcess.desiredDate')}
                id="product-process-desiredDate"
                name="desiredDate"
                data-cy="desiredDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcess.actualStartDate')}
                id="product-process-actualStartDate"
                name="actualStartDate"
                data-cy="actualStartDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcess.transactionId')}
                id="product-process-transactionId"
                name="transactionId"
                data-cy="transactionId"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcess.waiveCoolingOffRight')}
                id="product-process-waiveCoolingOffRight"
                name="waiveCoolingOffRight"
                data-cy="waiveCoolingOffRight"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcess.depositRequired')}
                id="product-process-depositRequired"
                name="depositRequired"
                data-cy="depositRequired"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcess.deposit')}
                id="product-process-deposit"
                name="deposit"
                data-cy="deposit"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcess.createdAt')}
                id="product-process-createdAt"
                name="createdAt"
                data-cy="createdAt"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcess.updatedAt')}
                id="product-process-updatedAt"
                name="updatedAt"
                data-cy="updatedAt"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <ValidatedField
                id="product-process-product"
                name="product"
                data-cy="product"
                label={translate('crawlerBackendApp.productProcess.product')}
                type="select"
              >
                <option value="" key="0" />
                {products
                  ? products.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button
                tag={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/product-process"
                replace
                color="info"
              >
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                type="submit"
                disabled={updating}
              >
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ProductProcessUpdate;
